/**
 * FormAnalyzer Admin Page
 *
 * Phase 6.2 Admin UI for managing AI-generated form schemas.
 * Allows admins to:
 * - View schema statistics by status and parsing method
 * - Review schemas needing attention (low confidence, draft status)
 * - Upload new PDF forms for analysis
 * - Navigate to individual schema review/editing
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  FileText,
  Upload,
  CheckCircle,
  AlertCircle,
  Clock,
  Bot,
  User,
  RefreshCw,
  Search,
  Filter,
  ChevronRight,
  Eye,
  Edit3,
  Trash2,
  ArrowUpCircle,
  X,
  Loader2,
  Sparkles
} from 'lucide-react';
import { AdminPageHeader, AdminCard, AdminStatCard } from '@/components/admin/AdminShell';
import { supabase } from '@/lib/supabase';
import { useAnalyzeFormPdf } from '@/hooks/useFormSchemas';
import type { ParsedFormField } from '@/types/smart-schema';

// =============================================================================
// TYPES
// =============================================================================

interface FormSchemaRow {
  id: string;
  form_id: string;
  form_name: string;
  jurisdiction_code: string;
  permit_type: string;
  version: string;
  fields: ParsedFormField[];
  sections: Array<{ id: string; name: string; order: number; fields: string[] }>;
  metadata: {
    pageCount: number;
    totalFields: number;
    aiConfidenceAvg: number;
    needsReview: string[];
  };
  status: 'draft' | 'analyzing' | 'review' | 'approved' | 'archived';
  parsed_by: 'ai' | 'manual' | 'ai_reviewed';
  pdf_storage_path: string | null;
  pdf_filename: string | null;
  ai_confidence_avg: number | null;
  reviewed_by: string | null;
  reviewed_at: string | null;
  created_at: string;
  updated_at: string;
  approved_at: string | null;
}

interface SchemaStats {
  total: number;
  byStatus: {
    draft: number;
    analyzing: number;
    review: number;
    approved: number;
    archived: number;
  };
  byParsedBy: {
    ai: number;
    manual: number;
    ai_reviewed: number;
  };
  avgConfidence: number | null;
  totalFields: number;
}

type StatusFilter = 'all' | 'draft' | 'analyzing' | 'review' | 'approved' | 'archived';

// =============================================================================
// COMPONENT
// =============================================================================

export default function FormAnalyzer() {
  // State
  const [schemas, setSchemas] = useState<FormSchemaRow[]>([]);
  const [stats, setStats] = useState<SchemaStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [uploading, setUploading] = useState(false);

  // Upload dialog state
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const [uploadFormData, setUploadFormData] = useState({
    jurisdictionCode: '',
    permitType: 'general',
    formName: '',
  });
  const fileInputRef = useRef<HTMLInputElement>(null);

  // AI Analysis hook
  const analyzeFormPdf = useAnalyzeFormPdf();

  // Fetch schemas and stats
  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      // Fetch all schemas
      const { data: schemasData, error: schemasError } = await supabase
        .from('form_schemas')
        .select('*')
        .order('updated_at', { ascending: false });

      if (schemasError) throw schemasError;

      const rows = (schemasData || []) as FormSchemaRow[];
      setSchemas(rows);

      // Calculate stats
      const statsResult: SchemaStats = {
        total: rows.length,
        byStatus: {
          draft: rows.filter(r => r.status === 'draft').length,
          analyzing: rows.filter(r => r.status === 'analyzing').length,
          review: rows.filter(r => r.status === 'review').length,
          approved: rows.filter(r => r.status === 'approved').length,
          archived: rows.filter(r => r.status === 'archived').length,
        },
        byParsedBy: {
          ai: rows.filter(r => r.parsed_by === 'ai').length,
          manual: rows.filter(r => r.parsed_by === 'manual').length,
          ai_reviewed: rows.filter(r => r.parsed_by === 'ai_reviewed').length,
        },
        avgConfidence: rows.length > 0
          ? rows.reduce((sum, r) => sum + (r.ai_confidence_avg || 0), 0) / rows.filter(r => r.ai_confidence_avg).length
          : null,
        totalFields: rows.reduce((sum, r) => sum + (r.metadata?.totalFields || 0), 0),
      };
      setStats(statsResult);
    } catch (err) {
      console.error('Error fetching form schemas:', err);
      setError(err instanceof Error ? err.message : 'Failed to load form schemas');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Filter schemas
  const filteredSchemas = schemas.filter(schema => {
    // Status filter
    if (statusFilter !== 'all' && schema.status !== statusFilter) {
      return false;
    }
    // Search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      return (
        schema.form_name.toLowerCase().includes(term) ||
        schema.jurisdiction_code.toLowerCase().includes(term) ||
        schema.permit_type.toLowerCase().includes(term) ||
        schema.form_id.toLowerCase().includes(term)
      );
    }
    return true;
  });

  // Schemas needing review (low confidence or review status)
  const needingReview = schemas.filter(
    s => s.status === 'review' || (s.ai_confidence_avg !== null && s.ai_confidence_avg < 0.7)
  );

  // Handle file selection - opens the dialog
  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.name.toLowerCase().endsWith('.pdf')) {
      setError('Please upload a PDF file');
      event.target.value = '';
      return;
    }

    // Store file and open dialog
    setPendingFile(file);
    setUploadFormData({
      jurisdictionCode: '',
      permitType: 'general',
      formName: file.name.replace('.pdf', ''),
    });
    setUploadDialogOpen(true);
    event.target.value = '';
  };

  // Handle dialog close
  const handleUploadDialogClose = () => {
    setUploadDialogOpen(false);
    setPendingFile(null);
    setUploadFormData({ jurisdictionCode: '', permitType: 'general', formName: '' });
  };

  // Handle AI analysis submission
  const handleAnalyzeSubmit = async () => {
    if (!pendingFile || !uploadFormData.jurisdictionCode || !uploadFormData.formName) {
      setError('Please fill in all required fields');
      return;
    }

    setUploading(true);
    setError(null);
    setUploadDialogOpen(false);

    try {
      // Convert PDF to base64
      const arrayBuffer = await pendingFile.arrayBuffer();
      const base64 = btoa(
        new Uint8Array(arrayBuffer).reduce((data, byte) => data + String.fromCharCode(byte), '')
      );

      // Call AI analysis Cloud Function
      const result = await analyzeFormPdf.mutateAsync({
        pdfBase64: base64,
        jurisdictionCode: uploadFormData.jurisdictionCode,
        permitType: uploadFormData.permitType,
        formName: uploadFormData.formName,
        saveToDatabase: true,
      });

      if (result.success) {
        // Refresh data to show the new schema
        await fetchData();

        // Show success info
        const { stats: analysisStats } = result;
        console.log('Analysis complete:', {
          totalFields: analysisStats.totalFields,
          highConfidence: analysisStats.highConfidence,
          needsReview: analysisStats.needsReview,
          processingTimeMs: analysisStats.processingTimeMs,
        });
      }
    } catch (err) {
      console.error('Error analyzing PDF:', err);
      setError(err instanceof Error ? err.message : 'Failed to analyze PDF');
    } finally {
      setUploading(false);
      setPendingFile(null);
      setUploadFormData({ jurisdictionCode: '', permitType: 'general', formName: '' });
    }
  };

  // Delete schema
  const handleDelete = async (schemaId: string) => {
    if (!confirm('Are you sure you want to delete this schema?')) return;

    try {
      const { error: deleteError } = await supabase
        .from('form_schemas')
        .delete()
        .eq('id', schemaId);

      if (deleteError) throw deleteError;

      await fetchData();
    } catch (err) {
      console.error('Error deleting schema:', err);
      setError(err instanceof Error ? err.message : 'Failed to delete schema');
    }
  };

  // Get status badge color
  const getStatusColor = (status: FormSchemaRow['status']) => {
    switch (status) {
      case 'draft': return 'bg-slate-600 text-slate-100';
      case 'analyzing': return 'bg-blue-600 text-blue-100';
      case 'review': return 'bg-yellow-600 text-yellow-100';
      case 'approved': return 'bg-green-600 text-green-100';
      case 'archived': return 'bg-gray-600 text-gray-100';
      default: return 'bg-gray-600 text-gray-100';
    }
  };

  // Get confidence badge color
  const getConfidenceColor = (confidence: number | null) => {
    if (confidence === null) return 'text-slate-400';
    if (confidence >= 0.85) return 'text-green-400';
    if (confidence >= 0.6) return 'text-yellow-400';
    return 'text-red-400';
  };

  // Format date
  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <div className="space-y-6">
      <AdminPageHeader
        title="Form Analyzer"
        description="Manage AI-generated form schemas and field mappings"
        actions={
          <div className="flex items-center gap-3">
            <button
              onClick={fetchData}
              disabled={loading}
              className="flex items-center gap-2 px-3 py-2 text-sm bg-[hsl(220,15%,20%)] hover:bg-[hsl(220,15%,25%)] rounded-lg transition-colors disabled:opacity-50"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
            <label className="flex items-center gap-2 px-4 py-2 text-sm bg-blue-600 hover:bg-blue-700 rounded-lg cursor-pointer transition-colors">
              <Upload className="w-4 h-4" />
              {uploading ? 'Uploading...' : 'Upload PDF'}
              <input
                type="file"
                accept=".pdf"
                className="hidden"
                onChange={handleFileUpload}
                disabled={uploading}
              />
            </label>
          </div>
        }
      />

      {/* Error Alert */}
      {error && (
        <div className="bg-red-900/20 border border-red-500/30 rounded-lg p-4 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-red-400 font-medium">Error</p>
            <p className="text-red-300/80 text-sm">{error}</p>
          </div>
        </div>
      )}

      {/* Stats Grid */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
          <AdminStatCard
            label="Total Schemas"
            value={stats.total.toString()}
            icon={<FileText className="w-5 h-5" />}
          />
          <AdminStatCard
            label="Pending Review"
            value={stats.byStatus.review.toString()}
            icon={<Clock className="w-5 h-5" />}
            changeType={stats.byStatus.review > 0 ? 'negative' : 'neutral'}
          />
          <AdminStatCard
            label="Approved"
            value={stats.byStatus.approved.toString()}
            icon={<CheckCircle className="w-5 h-5" />}
            changeType="positive"
          />
          <AdminStatCard
            label="AI Parsed"
            value={stats.byParsedBy.ai.toString()}
            icon={<Bot className="w-5 h-5" />}
          />
          <AdminStatCard
            label="AI Reviewed"
            value={stats.byParsedBy.ai_reviewed.toString()}
            icon={<User className="w-5 h-5" />}
          />
          <AdminStatCard
            label="Total Fields"
            value={stats.totalFields.toString()}
            icon={<Edit3 className="w-5 h-5" />}
          />
        </div>
      )}

      {/* Needs Review Alert */}
      {needingReview.length > 0 && (
        <AdminCard className="border-yellow-500/30 bg-yellow-900/10">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-yellow-400 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <h3 className="text-yellow-400 font-medium">
                {needingReview.length} Schema{needingReview.length !== 1 ? 's' : ''} Need Review
              </h3>
              <p className="text-yellow-300/70 text-sm mt-1">
                These schemas have low AI confidence or are awaiting manual review.
              </p>
              <div className="mt-3 flex flex-wrap gap-2">
                {needingReview.slice(0, 5).map(schema => (
                  <a
                    key={schema.id}
                    href={`/admin/form-analyzer/${schema.id}`}
                    className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs bg-yellow-600/20 hover:bg-yellow-600/30 text-yellow-300 rounded-md transition-colors"
                  >
                    {schema.form_name}
                    <ChevronRight className="w-3 h-3" />
                  </a>
                ))}
                {needingReview.length > 5 && (
                  <span className="text-yellow-400/60 text-xs py-1">
                    +{needingReview.length - 5} more
                  </span>
                )}
              </div>
            </div>
          </div>
        </AdminCard>
      )}

      {/* Filters */}
      <AdminCard>
        <div className="flex flex-col sm:flex-row gap-4">
          {/* Search */}
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input
              type="text"
              placeholder="Search by name, jurisdiction, or permit type..."
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 bg-[hsl(220,20%,10%)] border border-[hsl(220,15%,25%)] rounded-lg text-sm focus:outline-none focus:border-blue-500"
            />
          </div>
          {/* Status Filter */}
          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4 text-slate-400" />
            <select
              value={statusFilter}
              onChange={e => setStatusFilter(e.target.value as StatusFilter)}
              className="px-3 py-2 bg-[hsl(220,20%,10%)] border border-[hsl(220,15%,25%)] rounded-lg text-sm focus:outline-none focus:border-blue-500"
            >
              <option value="all">All Statuses</option>
              <option value="draft">Draft</option>
              <option value="analyzing">Analyzing</option>
              <option value="review">Review</option>
              <option value="approved">Approved</option>
              <option value="archived">Archived</option>
            </select>
          </div>
        </div>
      </AdminCard>

      {/* Schemas Table */}
      <AdminCard>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-[hsl(220,15%,20%)]">
                <th className="text-left py-3 px-4 text-sm font-medium text-slate-400">Form</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-slate-400">Jurisdiction</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-slate-400">Type</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-slate-400">Status</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-slate-400">Fields</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-slate-400">Confidence</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-slate-400">Updated</th>
                <th className="text-right py-3 px-4 text-sm font-medium text-slate-400">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[hsl(220,15%,15%)]">
              {loading && (
                <tr>
                  <td colSpan={8} className="py-12 text-center text-slate-400">
                    <RefreshCw className="w-6 h-6 animate-spin mx-auto mb-2" />
                    Loading schemas...
                  </td>
                </tr>
              )}
              {!loading && filteredSchemas.length === 0 && (
                <tr>
                  <td colSpan={8} className="py-12 text-center text-slate-400">
                    <FileText className="w-8 h-8 mx-auto mb-2 opacity-50" />
                    {searchTerm || statusFilter !== 'all'
                      ? 'No schemas match your filters'
                      : 'No form schemas yet. Upload a PDF to get started.'}
                  </td>
                </tr>
              )}
              {!loading && filteredSchemas.map(schema => (
                <tr
                  key={schema.id}
                  className="hover:bg-[hsl(220,15%,12%)] transition-colors"
                >
                  <td className="py-3 px-4">
                    <div>
                      <p className="font-medium text-slate-100">{schema.form_name}</p>
                      <p className="text-xs text-slate-500 font-mono">{schema.form_id}</p>
                    </div>
                  </td>
                  <td className="py-3 px-4">
                    <span className="text-sm text-slate-300">{schema.jurisdiction_code}</span>
                  </td>
                  <td className="py-3 px-4">
                    <span className="text-sm text-slate-300 capitalize">{schema.permit_type}</span>
                  </td>
                  <td className="py-3 px-4">
                    <span className={`inline-flex items-center px-2 py-0.5 text-xs font-medium rounded ${getStatusColor(schema.status)}`}>
                      {schema.status}
                    </span>
                    {schema.parsed_by === 'ai' && (
                      <Bot className="inline w-3.5 h-3.5 ml-1.5 text-blue-400" title="AI Parsed" />
                    )}
                  </td>
                  <td className="py-3 px-4">
                    <span className="text-sm text-slate-300">
                      {schema.metadata?.totalFields || schema.fields?.length || 0}
                    </span>
                  </td>
                  <td className="py-3 px-4">
                    <span className={`text-sm font-medium ${getConfidenceColor(schema.ai_confidence_avg)}`}>
                      {schema.ai_confidence_avg !== null
                        ? `${(schema.ai_confidence_avg * 100).toFixed(0)}%`
                        : '—'}
                    </span>
                  </td>
                  <td className="py-3 px-4">
                    <span className="text-sm text-slate-400">{formatDate(schema.updated_at)}</span>
                  </td>
                  <td className="py-3 px-4">
                    <div className="flex items-center justify-end gap-1">
                      <a
                        href={`/admin/form-analyzer/${schema.id}`}
                        className="p-1.5 text-slate-400 hover:text-slate-100 hover:bg-[hsl(220,15%,20%)] rounded transition-colors"
                        title="View Details"
                      >
                        <Eye className="w-4 h-4" />
                      </a>
                      <a
                        href={`/admin/form-analyzer/${schema.id}/edit`}
                        className="p-1.5 text-slate-400 hover:text-blue-400 hover:bg-[hsl(220,15%,20%)] rounded transition-colors"
                        title="Edit Schema"
                      >
                        <Edit3 className="w-4 h-4" />
                      </a>
                      {schema.status !== 'approved' && (
                        <button
                          onClick={() => handleDelete(schema.id)}
                          className="p-1.5 text-slate-400 hover:text-red-400 hover:bg-[hsl(220,15%,20%)] rounded transition-colors"
                          title="Delete Schema"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                      {schema.status === 'review' && (
                        <a
                          href={`/admin/form-analyzer/${schema.id}/approve`}
                          className="p-1.5 text-slate-400 hover:text-green-400 hover:bg-[hsl(220,15%,20%)] rounded transition-colors"
                          title="Approve Schema"
                        >
                          <ArrowUpCircle className="w-4 h-4" />
                        </a>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </AdminCard>

      {/* Upload Hint */}
      {!loading && schemas.length === 0 && (
        <div className="text-center py-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-[hsl(220,15%,15%)] rounded-full mb-4">
            <Upload className="w-8 h-8 text-slate-400" />
          </div>
          <h3 className="text-lg font-medium text-slate-100 mb-2">Get Started</h3>
          <p className="text-slate-400 max-w-md mx-auto">
            Upload a permit PDF form to analyze its fields and create semantic mappings.
            AI will extract fields and suggest mappings to canonical data.
          </p>
        </div>
      )}

      {/* Upload Dialog */}
      {uploadDialogOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            onClick={handleUploadDialogClose}
          />

          {/* Dialog */}
          <div className="relative w-full max-w-md bg-[hsl(220,15%,13%)] border border-[hsl(220,15%,25%)] rounded-xl shadow-xl">
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-[hsl(220,15%,20%)]">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-600/20 rounded-lg">
                  <Sparkles className="w-5 h-5 text-blue-400" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-slate-100">AI Form Analysis</h2>
                  <p className="text-sm text-slate-400">Configure before analyzing</p>
                </div>
              </div>
              <button
                onClick={handleUploadDialogClose}
                className="p-1.5 text-slate-400 hover:text-slate-100 hover:bg-[hsl(220,15%,20%)] rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Content */}
            <div className="px-6 py-5 space-y-4">
              {/* File info */}
              {pendingFile && (
                <div className="flex items-center gap-3 p-3 bg-[hsl(220,15%,10%)] rounded-lg">
                  <FileText className="w-8 h-8 text-blue-400" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-slate-100 truncate">
                      {pendingFile.name}
                    </p>
                    <p className="text-xs text-slate-400">
                      {(pendingFile.size / 1024).toFixed(1)} KB
                    </p>
                  </div>
                </div>
              )}

              {/* Form Name */}
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-1.5">
                  Form Name <span className="text-red-400">*</span>
                </label>
                <input
                  type="text"
                  value={uploadFormData.formName}
                  onChange={(e) => setUploadFormData(prev => ({ ...prev, formName: e.target.value }))}
                  placeholder="e.g., Building Permit Application"
                  className="w-full px-3 py-2 bg-[hsl(220,20%,10%)] border border-[hsl(220,15%,25%)] rounded-lg text-sm text-slate-100 placeholder-slate-500 focus:outline-none focus:border-blue-500"
                />
              </div>

              {/* Jurisdiction Code */}
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-1.5">
                  Jurisdiction Code <span className="text-red-400">*</span>
                </label>
                <input
                  type="text"
                  value={uploadFormData.jurisdictionCode}
                  onChange={(e) => setUploadFormData(prev => ({ ...prev, jurisdictionCode: e.target.value.toUpperCase() }))}
                  placeholder="e.g., FL-DUVAL, FL-MIAMI-DADE"
                  className="w-full px-3 py-2 bg-[hsl(220,20%,10%)] border border-[hsl(220,15%,25%)] rounded-lg text-sm text-slate-100 placeholder-slate-500 focus:outline-none focus:border-blue-500"
                />
                <p className="mt-1 text-xs text-slate-500">
                  Use format: STATE-COUNTY or STATE-CITY
                </p>
              </div>

              {/* Permit Type */}
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-1.5">
                  Permit Type
                </label>
                <select
                  value={uploadFormData.permitType}
                  onChange={(e) => setUploadFormData(prev => ({ ...prev, permitType: e.target.value }))}
                  className="w-full px-3 py-2 bg-[hsl(220,20%,10%)] border border-[hsl(220,15%,25%)] rounded-lg text-sm text-slate-100 focus:outline-none focus:border-blue-500"
                >
                  <option value="general">General / Building</option>
                  <option value="electrical">Electrical</option>
                  <option value="plumbing">Plumbing</option>
                  <option value="mechanical">Mechanical / HVAC</option>
                  <option value="roofing">Roofing</option>
                  <option value="demolition">Demolition</option>
                  <option value="fence">Fence</option>
                  <option value="pool">Pool</option>
                </select>
              </div>

              {/* Info note */}
              <div className="flex items-start gap-2 p-3 bg-blue-900/20 border border-blue-500/20 rounded-lg">
                <Bot className="w-4 h-4 text-blue-400 flex-shrink-0 mt-0.5" />
                <p className="text-xs text-blue-300">
                  GPT-4 Vision will analyze the PDF, extract form fields, and suggest semantic mappings.
                  You can review and adjust the results afterward.
                </p>
              </div>
            </div>

            {/* Footer */}
            <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-[hsl(220,15%,20%)]">
              <button
                onClick={handleUploadDialogClose}
                className="px-4 py-2 text-sm text-slate-300 hover:text-slate-100 hover:bg-[hsl(220,15%,20%)] rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleAnalyzeSubmit}
                disabled={uploading || !uploadFormData.jurisdictionCode || !uploadFormData.formName}
                className="flex items-center gap-2 px-4 py-2 text-sm font-medium bg-blue-600 hover:bg-blue-700 disabled:bg-blue-600/50 disabled:cursor-not-allowed rounded-lg transition-colors"
              >
                {uploading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Analyzing...
                  </>
                ) : (
                  <>
                    <Sparkles className="w-4 h-4" />
                    Analyze with AI
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Analyzing Overlay */}
      {uploading && !uploadDialogOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />
          <div className="relative flex flex-col items-center gap-4 p-8 bg-[hsl(220,15%,13%)] border border-[hsl(220,15%,25%)] rounded-xl">
            <div className="relative">
              <div className="absolute inset-0 animate-ping">
                <Sparkles className="w-12 h-12 text-blue-400/30" />
              </div>
              <Sparkles className="w-12 h-12 text-blue-400" />
            </div>
            <div className="text-center">
              <h3 className="text-lg font-semibold text-slate-100">Analyzing PDF</h3>
              <p className="text-sm text-slate-400 mt-1">
                GPT-4 Vision is extracting and mapping form fields...
              </p>
            </div>
            <div className="flex items-center gap-2 text-sm text-slate-500">
              <Loader2 className="w-4 h-4 animate-spin" />
              This may take 30-60 seconds
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
